import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Check, Sparkles, X } from "lucide-react";
import { api } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import AttachmentList from "../components/AttachmentList";
import AttachmentUpload from "../components/AttachmentUpload";
import CommentThread from "../components/CommentThread";
import FeedbackWidget from "../components/FeedbackWidget";
import ClosureHistory from "../components/ClosureHistory";
import ResolutionDialog from "../components/ResolutionDialog";
import AssignDialog from "../components/AssignDialog";

// v1.9: implemented/completed/rejected require closure notes captured via dialog.
const SELECTABLE = ["awaiting_authorization", "build_test", "awaiting_implementation"];
const CLOSURE_DIALOG_CONFIG = {
  implemented: { title: "Mark Implemented", actionLabel: "Mark Implemented", fieldLabel: "Implementation notes", tone: "primary", required: true },
  completed:   { title: "Complete change",  actionLabel: "Complete change",  fieldLabel: "Implementation notes", tone: "primary", required: true },
};

export default function ChangeRequestDetail() {
  const { id } = useParams();
  const { user, hasRole } = useAuth();
  const [cr, setCr] = useState(null);
  const [users, setUsers] = useState([]);
  const [notes, setNotes] = useState("");
  const [tests, setTests] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [cabNotes, setCabNotes] = useState("");
  const [pirSummary, setPirSummary] = useState("");
  const [pirLessons, setPirLessons] = useState("");
  const [newAttachments, setNewAttachments] = useState([]);
  const [dialogAction, setDialogAction] = useState(null); // 'implemented' | 'completed'
  // v2.2 #1: in-app Assign modal
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSuggestion, setAssignSuggestion] = useState(null);

  async function load() {
    const { data } = await api.get(`/api/change-requests/${id}`); setCr(data);
    setNotes(data.approval_notes || ""); setTests(data.test_results || "");
    setScheduleAt(data.scheduled_implementation_at ? data.scheduled_implementation_at.slice(0, 16) : "");
    setCabNotes(data.cab_notes || "");
    setPirSummary(data.pir_summary || ""); setPirLessons(data.pir_lessons_learned || "");
  }
  useEffect(() => {
    load();
    if (hasRole("admin", "it_team", "it_manager", "it_security")) api.get("/api/users").then((r) => setUsers(r.data));
  }, [id]); // eslint-disable-line

  async function update(payload) {
    try { const { data } = await api.put(`/api/change-requests/${id}`, payload); setCr(data); return data; }
    catch (e) { alert(e.response?.data?.detail || "Update failed"); throw e; }
  }

  async function openAssignDialog() {
    try {
      const { data } = await api.get(`/api/users/suggest-assignee?ticket_type=change_request`);
      if (data.assignee) {
        setAssignSuggestion({ ...data.assignee, open_count: data.open_count, category_familiarity: data.category_familiarity });
      } else {
        setAssignSuggestion(null);
      }
    } catch {
      setAssignSuggestion(null);
    }
    setAssignOpen(true);
  }
  async function handleAssignSubmit(userId) {
    await update({ assigned_to_id: userId });
    setAssignOpen(false);
  }

  async function handleDialogSubmit(text) {
    if (!dialogAction) return;
    await update({ status: dialogAction, implementation_notes: text });
    setDialogAction(null);
  }

  if (!cr) return <div>Loading…</div>;

  // v2.8: admin can approve as any role
  const itMgrCan = hasRole("admin", "it_manager") && cr.it_manager_approved === null;
  const cfoCan = hasRole("admin", "cfo") && cr.cfo_approved === null;
  const itSecCan = hasRole("admin", "it_security") && cr.it_security_approved === null;
  const isIT = hasRole("admin", "it_team", "it_manager");
  const bothApproved =
    cr.it_manager_approved === true &&
    cr.cfo_approved === true &&
    cr.it_security_approved === true;

  const dlg = dialogAction ? CLOSURE_DIALOG_CONFIG[dialogAction] : null;
  // v2.0 #8
  const isClosedCr = ["implemented", "completed", "rejected"].includes(cr.status);

  return (
    <div className="space-y-4">
      <Link to="/change-requests" className="text-sm text-brand-600 hover:underline flex items-center gap-1"><ArrowLeft size={14} /> Back</Link>
      <div className="card p-5">
        <div className="flex justify-between">
          <div>
            <div className="text-xs font-mono text-gray-500">{cr.change_number}</div>
            <h1 className="text-xl font-bold">{cr.title}</h1>
            <div className="text-sm text-gray-600">Requester: {cr.requester_name} · Type: <b>{cr.change_type}</b> · Plant: {cr.plant}</div>
          </div>
          <span className="badge bg-brand-50 text-brand-700">{cr.status}</span>
        </div>
        <p className="mt-4 text-sm whitespace-pre-wrap">{cr.description}</p>
      </div>

      {(itMgrCan || cfoCan || itSecCan) && (
        <div className="card p-5 space-y-4">
          <h2 className="font-semibold">Approvals you can act on</h2>
          <textarea className="input" rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Approval notes (shared across your approvals)" />
          {[
            { key: "it_manager_approved",  label: "IT Manager",  can: itMgrCan },
            { key: "cfo_approved",         label: "CFO",         can: cfoCan },
            { key: "it_security_approved", label: "IT Security", can: itSecCan },
          ].filter((r) => r.can).map((r) => (
            <div key={r.key} className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
              <div className="text-sm font-semibold text-slate-700">{r.label} approval</div>
              <div className="flex gap-2">
                <button className="btn-primary flex items-center gap-1" onClick={() => update({ [r.key]: true, approval_notes: notes })}>
                  <Check size={14} /> Approve
                </button>
                <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded flex items-center gap-1" onClick={() => update({ [r.key]: false, approval_notes: notes })}>
                  <X size={14} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card p-5">
        <h2 className="font-semibold mb-2">Approval state</h2>
        <ul className="text-sm space-y-1">
          <li>IT Manager: <b>{cr.it_manager_approved === true ? "Approved" : cr.it_manager_approved === false ? "Rejected" : "Pending"}</b> <span className="text-xs text-gray-500">(gates build_test)</span></li>
          <li>CFO: <b>{cr.cfo_approved === true ? "Approved" : cr.cfo_approved === false ? "Rejected" : "Pending"}</b> <span className="text-xs text-gray-500">(gates build_test)</span></li>
          <li>IT Security: <b>{cr.it_security_approved === true ? "Approved" : cr.it_security_approved === false ? "Rejected" : "Pending"}</b> <span className="text-xs text-gray-500">(gates build_test)</span></li>
        </ul>
        {hasRole("admin", "it_manager") && (
          <div className="mt-4 pt-4 border-t">
            <h3 className="font-medium mb-2 text-sm text-gray-700">Pre-authorization stamps (Stage 1)</h3>
            <p className="text-xs text-gray-500 mb-3">Informational sign-offs from domain stakeholders. Doesn't gate workflow but documented for audit.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label text-xs">Key User</label>
                <select className="input py-1 text-sm" value={cr.key_user_approved === true ? "yes" : cr.key_user_approved === false ? "no" : ""}
                  onChange={(e) => update({ key_user_approved: e.target.value === "yes" ? true : e.target.value === "no" ? false : null })}>
                  <option value="">— Pending —</option>
                  <option value="yes">Approved</option>
                  <option value="no">Rejected</option>
                </select>
              </div>
              <div>
                <label className="label text-xs">Business Process Owner</label>
                <select className="input py-1 text-sm" value={cr.business_owner_approved === true ? "yes" : cr.business_owner_approved === false ? "no" : ""}
                  onChange={(e) => update({ business_owner_approved: e.target.value === "yes" ? true : e.target.value === "no" ? false : null })}>
                  <option value="">— Pending —</option>
                  <option value="yes">Approved</option>
                  <option value="no">Rejected</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {hasRole("admin", "it_manager") && bothApproved && (
        <div className="card p-5 space-y-3">
          <h2 className="font-semibold flex items-center gap-2"><Calendar size={16} /> CAB Schedule</h2>
          <p className="text-xs text-gray-500">Used for high-risk changes. Once set, this CR appears on the CAB Calendar.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Scheduled implementation</label>
              <input type="datetime-local" className="input" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} />
            </div>
            <div>
              <label className="label">CAB notes</label>
              <input className="input" value={cabNotes} onChange={(e) => setCabNotes(e.target.value)} placeholder="Maintenance window, comms plan, rollback owner…" />
            </div>
          </div>
          <button className="btn-secondary"
            onClick={() => update({ scheduled_implementation_at: scheduleAt ? new Date(scheduleAt).toISOString() : null, cab_notes: cabNotes })}>
            Save schedule
          </button>
        </div>
      )}

      {isIT && bothApproved && (
        <div className="card p-5 space-y-3">
          <h2 className="font-semibold">Build / Test / Implement</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label flex items-center justify-between">
                <span>Assignee</span>
                {!isClosedCr && (
                  <button type="button" onClick={openAssignDialog} className="text-xs text-brand-600 hover:underline flex items-center gap-1">
                    <Sparkles size={12} /> Suggest by workload
                  </button>
                )}
              </label>
              {isClosedCr ? (
                <div className="input bg-slate-50 text-slate-600 cursor-not-allowed">
                  {cr.assigned_to_name || "— Unassigned —"}
                  <span className="ml-2 text-xs text-slate-500">(this change is closed)</span>
                </div>
              ) : (
                <select className="input" value={cr.assigned_to_id || ""}
                  onChange={(e) => update({ assigned_to_id: e.target.value || null })}>
                  <option value="">— Unassigned —</option>
                  {users.filter((u) => ["it_team", "it_manager", "it_security"].includes(u.role))
                    .map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="label">Status (raw)</label>
              <select className="input" value={SELECTABLE.includes(cr.status) ? cr.status : ""}
                onChange={(e) => { if (e.target.value) update({ status: e.target.value }); }}>
                {!SELECTABLE.includes(cr.status) && <option value="">{cr.status} (closure)</option>}
                {SELECTABLE.map((s) => <option key={s}>{s}</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Implementation / Completion captures permanent notes — use the buttons below.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="label">Test results</label>
              <textarea className="input" rows="2" value={tests} onChange={(e) => setTests(e.target.value)} />
              <button className="btn-secondary mt-2" onClick={() => update({ test_results: tests })}>Save</button>
            </div>
            <div className="sm:col-span-2 flex flex-wrap gap-2">
              {!["implemented", "completed", "rejected"].includes(cr.status) && (
                <>
                  <button className="btn-primary" onClick={() => setDialogAction("implemented")}>Mark Implemented…</button>
                  <button className="btn-primary" onClick={() => setDialogAction("completed")}>Complete Change…</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PIR — Post-Implementation Review (visible after implemented/completed) */}
      {(["implemented", "completed"].includes(cr.status) || cr.pir_summary) && (
        <div className="card p-5 space-y-3">
          <h2 className="font-semibold">Post-Implementation Review (PIR)</h2>
          <div className="text-xs text-gray-500">
            {cr.pir_completed_at ? (
              <>Completed {new Date(cr.pir_completed_at).toLocaleString()}</>
            ) : cr.change_type === "emergency" ? (
              "Required for emergency changes."
            ) : (
              "Optional — recommended for high-impact changes."
            )}
          </div>
          {isIT ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={!!cr.pir_required} onChange={(e) => update({ pir_required: e.target.checked })} />
                <label className="text-sm">PIR required</label>
              </div>
              <div>
                <label className="label">Summary</label>
                <textarea className="input" rows="3" value={pirSummary} onChange={(e) => setPirSummary(e.target.value)} placeholder="Outcome, timeline, any deviations…" />
                <button className="btn-secondary mt-1" onClick={() => update({ pir_summary: pirSummary })}>Save summary</button>
              </div>
              <div>
                <label className="label">Lessons learned</label>
                <textarea className="input" rows="3" value={pirLessons} onChange={(e) => setPirLessons(e.target.value)} placeholder="What we'd do differently next time…" />
                <button className="btn-secondary mt-1" onClick={() => update({ pir_lessons_learned: pirLessons })}>Save lessons</button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-700 space-y-2">
              {cr.pir_summary && <div><b>Summary:</b><br />{cr.pir_summary}</div>}
              {cr.pir_lessons_learned && <div><b>Lessons learned:</b><br />{cr.pir_lessons_learned}</div>}
              {!cr.pir_summary && !cr.pir_lessons_learned && <div className="text-gray-500">No PIR yet.</div>}
            </div>
          )}
        </div>
      )}

      {hasRole("admin", "it_team", "it_manager") && (
        <div className="card p-5 space-y-2">
          <h2 className="font-semibold">Add attachments</h2>
          <AttachmentUpload value={newAttachments} onChange={setNewAttachments} />
          {newAttachments.length > 0 && (
            <button className="btn-primary" onClick={async () => { await update({ attachments: newAttachments }); setNewAttachments([]); }}>
              Upload {newAttachments.length} file(s)
            </button>
          )}
        </div>
      )}

      <AttachmentList items={cr.attachments} />

      <ClosureHistory entries={cr.closure_history || []} title="Implementation / Closure history" />

      <CommentThread kind="change-requests" ticket={cr} onPosted={load} />

      <FeedbackWidget kind="change-requests" ticket={cr} currentUser={user} onSubmitted={() => load()} />

      {dlg && (
        <ResolutionDialog
          open={!!dialogAction}
          onClose={() => setDialogAction(null)}
          onSubmit={handleDialogSubmit}
          title={dlg.title}
          fieldLabel={dlg.fieldLabel}
          actionLabel={dlg.actionLabel}
          tone={dlg.tone}
          required={dlg.required}
        />
      )}

      <AssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSubmit={handleAssignSubmit}
        suggested={assignSuggestion}
        users={users}
        allowedRoles={["it_team", "it_manager", "it_security"]}
        ticketCategory={cr.change_type}
        ticketNumber={cr.change_number}
      />
    </div>
  );
}
